/**
 * AboutDialog — a native <dialog> About panel (Task 076). Replaces the dead
 * `#about` jump with a real, accessible surface.
 *
 * React discipline (docs/frontend-react-guidelines.md):
 *   - Open/close are event-handler-driven — NO Effect. App opens it with
 *     `dialogRef.current.showModal()` from a click handler; the close button
 *     and a backdrop click call `.close()`. A modal <dialog> handles Escape and
 *     the focus trap natively, and returns focus to the trigger on close.
 *   - The ref is imperative DOM access (allowed), not state mirroring — there is
 *     no React "isOpen" state to keep in sync.
 */
import type { RefObject, MouseEvent } from "react";
import type { Strings } from "../strings";

export interface AboutDialogProps {
  strings: Strings;
  /** Owned by App so its trigger handlers can call showModal()/close(). */
  dialogRef: RefObject<HTMLDialogElement>;
}

export function AboutDialog(props: AboutDialogProps): JSX.Element {
  const { strings, dialogRef } = props;

  function close(): void {
    dialogRef.current?.close();
  }

  // Close when the click lands on the dialog backdrop (the <dialog> element
  // itself) rather than the inner content panel.
  function handleDialogClick(event: MouseEvent<HTMLDialogElement>): void {
    if (event.target === dialogRef.current) {
      close();
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="about"
      aria-labelledby="about-title"
      onClick={handleDialogClick}
    >
      <div className="about__inner">
        <button
          type="button"
          className="about__close"
          onClick={close}
          aria-label={strings.aboutClose}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M6 6 L18 18 M18 6 L6 18"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <h2 id="about-title" className="about__title">
          {strings.aboutTitle}
        </h2>
        <div className="about__body">
          <p>{strings.aboutP1}</p>
          <p>{strings.aboutP2}</p>
          <p>{strings.aboutP3}</p>
          <p>{strings.aboutPrivacy}</p>
        </div>
        <div className="about__links">
          {/* Task 174: official congregation site primary; GreenScript secondary. */}
          <a
            className="about__official"
            href="https://losdelcamino.net/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {strings.navOfficialSite}
          </a>
          <a
            className="about__credit"
            href="https://greenscript.dev"
            target="_blank"
            rel="noopener noreferrer"
          >
            {strings.navDevelopedBy}
          </a>
        </div>
      </div>
    </dialog>
  );
}
